import styled from '@emotion/styled';
import {
  AdAnalyticsDailyPerformance,
  AdAnalyticsPlacementTypes,
  AdAnalyticsReport,
  AdAnalyticsVideo,
  AdPlacementKey,
  AdPlatformKey,
} from '@kaizenplatform/hugot-entity';
import { Button, Divider as _Divider, Modal, Paper, TextField as _TextField, Typography } from '@material-ui/core';
import { List } from 'immutable';
import { createElement, FC, Fragment, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import Language from '../../../entity/Language';
import Spinner from '../../component/compound/Spinner';
import { SCREEN_LARGE } from '../../constant/pageWidth';
import { SPACING_HUGE, SPACING_LARGE, SPACING_UNIT } from '../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import _AnalyticsItem from './../ShowAdAnalyticsPage/AnalyticsItem';
import _AnalyticsList from './../ShowAdAnalyticsPage/AnalyticsList';
import DailyPerformanceList from './../ShowAdAnalyticsPage/DailyPerformanceList';
import _Filters from './AdAnalyticsFilters';

type Props = {
  className?: string;
  loading: boolean;
  language: Language;
  submitComment: boolean;
  analyticsReport: AdAnalyticsReport | null;
  analyticsVideos: List<AdAnalyticsVideo> | null;
  placementTypes: AdAnalyticsPlacementTypes | null;
  dailyPerformance: List<AdAnalyticsDailyPerformance>;
  tryUpdateReport: ({ comment }: { comment: string }) => void;
  checkSubmitComment: () => void;
  onGotoAnalyticsReportListPage: () => void;
  onMount: () => void;
  onUnmount: () => void;
};

const ShowAdAnalyticsReportPage: FC<Props> = ({
  className,
  loading,
  language,
  submitComment,
  analyticsReport,
  analyticsVideos,
  dailyPerformance,
  placementTypes,
  tryUpdateReport,
  checkSubmitComment,
  onGotoAnalyticsReportListPage,
  onMount,
  onUnmount,
}: {
  className?: string;
  loading: boolean;
  language: Language;
  submitComment: boolean;
  analyticsReport: AdAnalyticsReport | null;
  analyticsVideos: List<AdAnalyticsVideo> | null;
  dailyPerformance: List<AdAnalyticsDailyPerformance>;
  placementTypes: AdAnalyticsPlacementTypes | null;
  tryUpdateReport: ({ comment }: { comment: string }) => void;
  checkSubmitComment: () => void;
  onGotoAnalyticsReportListPage: () => void;
  onMount: () => void;
  onUnmount: () => void;
}) => {
  useEffect(() => {
    onMount();
    return () => {
      onUnmount();
    };
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [modalOpen, updateModalOpen] = useState(false);
  const [comment, updateComment] = useState('');

  const handleCloseModal = () => {
    updateModalOpen(false);
    checkSubmitComment();
  };
  return (
    <Wrapper>
      <Root className={className}>
        {loading ? (
          <Loading>
            <Spinner />
          </Loading>
        ) : (
          <>
            <Title variant="h5">{analyticsReport ? analyticsReport.title : ''}</Title>
            <Container>
              <MenuWrapper>
                <Filters
                  placementTypes={placementTypes}
                  selectedPlacementTypes={{
                    platformKey:
                      analyticsReport && analyticsReport.platforms.size > 0
                        ? (analyticsReport.platforms.map((platforms) => platforms.key).toJS() as Array<AdPlatformKey>)
                        : [],
                    mediaKey:
                      analyticsReport && analyticsReport.medias.size > 0
                        ? (analyticsReport.medias.map((medias) => medias.key).toJS() as Array<AdPlatformKey>)
                        : [],
                    placementKey:
                      analyticsReport && analyticsReport.placements.size > 0
                        ? (analyticsReport.placements
                            .map((placements) => placements.key)
                            .toJS() as Array<AdPlacementKey>)
                        : [],
                    period: analyticsReport ? analyticsReport.period : undefined,
                  }}
                />
              </MenuWrapper>
              <>
                {analyticsVideos && analyticsVideos.size >= 1 ? (
                  <>
                    <AnalyticsList>
                      {analyticsVideos &&
                        analyticsVideos.map((analyticsVideo, index) => (
                          <Fragment key={`AnalyticsItem-${index}`}>
                            <AnalyticsItem analyticsVideo={analyticsVideo} language={language} />
                            <Divider />
                          </Fragment>
                        ))}
                    </AnalyticsList>
                    <DailyTitle>
                      <FormattedMessage id="adAnalytics_report_detail.label_daily_performance" />
                    </DailyTitle>
                    {dailyPerformance.size > 0 ? (
                      <>
                        <DailyPerformanceList dailyPerformance={dailyPerformance} />
                        <Divider />
                      </>
                    ) : (
                      <FormattedMessage id="adAnalytics_report_detail.label_undefined_performance" />
                    )}
                  </>
                ) : (
                  <EmptyState>
                    <Typography variant="h6" align="center">
                      <FormattedMessage id="adAnalytics_detail.empty_state" />
                    </Typography>
                    <PageBackButton variant="contained" color="primary" onClick={() => onGotoAnalyticsReportListPage()}>
                      <FormattedMessage id="adAnalytics_detail.page_back_button" />
                    </PageBackButton>
                  </EmptyState>
                )}
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => onGotoAnalyticsReportListPage()}
                  fullWidth={true}
                >
                  <FormattedMessage id="adAnalytics_report_detail.button_back_page" />
                </Button>
              </>
            </Container>
          </>
        )}
      </Root>
      {loading ? (
        <Loading>
          <Spinner />
        </Loading>
      ) : (
        <Comment>
          <TextField
            label={<FormattedMessage id="adAnalytics_report_detail.label_comment" />}
            onChange={(e) => updateComment(e.target.value)}
            multiline={true}
            rows={7}
            rowsMax={7}
            defaultValue={analyticsReport ? (analyticsReport.body ? analyticsReport.body : '') : ''}
          />
          <SubmitButton
            color="primary"
            variant="contained"
            disabled={comment === ''}
            onClick={() => {
              tryUpdateReport({ comment: comment });
            }}
          >
            <FormattedMessage id="adAnalytics_report_detail.button_update_comment" />
          </SubmitButton>
        </Comment>
      )}
      <Modal open={modalOpen || submitComment} onClose={handleCloseModal}>
        <ModalStyle>レポートのコメントを変更しました。</ModalStyle>
      </Modal>
    </Wrapper>
  );
};

const Wrapper = styled('div')({});

const Comment = styled('div')({
  height: '20%',
  border: '1px solid #ccc',
  position: 'fixed',
  width: '100%',
  maxWidth: '1080px',
  left: '50%',
  zIndex: 9999,
  bottom: 0,
  background: 'white',
  padding: '16px',
  marginLeft: '-50%',
  display: 'flex',
  alignItems: 'flex-end',
  '@media(min-width: 1080px)': {
    marginLeft: '-540px',
  },
});

const TextField = styled(_TextField)({
  width: '85%',
  marginRight: '8px',
});

const SubmitButton = styled(Button)({
  width: '15%',
  height: '42px',
});

const ModalStyle = styled('div')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '400',
  background: 'white',
  outline: 'none',
  boxShadow: '0 3px 7px rgba(0, 0, 0, 0.3)',
  borderRadius: '10px',
  border: '1px solid rgba(0, 0, 0, 0.3)',
  padding: '16px',
});

const Root = styled('section')({
  maxWidth: SCREEN_LARGE,
  margin: `0 auto ${SPACING_UNIT * 10}px`,
  paddingTop: `${SPACING_HUGE}px`,
  maxHeight: '80%',
  marginBottom: '20%',
});

const Title = styled(Typography)({
  marginBottom: SPACING_LARGE,
});

const Container = styled(Paper)(
  gridLayoutContainer({
    columns: ['100%'],
    rows: ['auto'],
    areas: [['filter'], ['analytics']],
    alignItems: 'center',
  }),
  {
    width: 'auto',
    padding: SPACING_LARGE,
  },
);

const MenuWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: SPACING_LARGE,
});

const AnalyticsList = styled(_AnalyticsList)(gridLayoutItem('analytics'));

const AnalyticsItem = styled(_AnalyticsItem)({
  marginBottom: SPACING_HUGE,
});

const Divider = styled(_Divider)({
  marginBottom: SPACING_HUGE,
});

const EmptyState = styled('div')({
  padding: SPACING_HUGE,
  textAlign: 'center',
});

const PageBackButton = styled(Button)({
  marginTop: SPACING_LARGE,
});

const DailyTitle = styled('div')({
  fontSize: '20px',
  color: 'rgba(0,0,0,.66)',
  marginBottom: '32px',
  fontWeight: 700,
});

const Loading = styled('div')({
  padding: SPACING_HUGE,
});

const Filters = styled(_Filters)(gridLayoutItem('filter'), {
  marginBottom: SPACING_LARGE,
});

export default ShowAdAnalyticsReportPage;
